import { useState } from "react";
import { Cpu } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LpfrDeviceRowDto } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

const fmt = (v: string | null) => (v ? new Date(v).toLocaleString("sr-Latn-RS", { dateStyle: "short", timeStyle: "short" }) : "—");
const OUTCOME: Record<string, string> = { active: "aktivan", active_bez_licence: "aktivan (bez licence)" };

function History({ id }: { id: string }) {
  const q = useQuery({ queryKey: ["lpfr-dev-hist", id], queryFn: () => api.adminLpfrDeviceHistory(id) });
  if (q.isLoading) return <p className="text-xs text-slate-400">Učitavanje...</p>;
  return (
    <ul className="mt-2 max-h-48 overflow-auto text-xs text-slate-600">
      {(q.data ?? []).map((h, i) => (
        <li key={i}>{fmt(h.at)} · {h.ip ?? "—"} · {OUTCOME[h.outcome] ?? h.outcome}{h.jid ? ` · ${h.jid}` : ""}{h.version ? ` · v${h.version}` : ""}</li>
      ))}
    </ul>
  );
}

function Card({ d, refresh }: { d: LpfrDeviceRowDto; refresh: () => void }) {
  const [hist, setHist] = useState(false);
  const toggle = useMutation({
    mutationFn: async (v: { type: string; value: string; on: boolean }) => {
      if (!v.on) return api.adminLpfrUnblock(v.type, v.value);
      const reason = window.prompt("Poruka koju korisnik vidi (neobavezno):", "") ?? "";
      return api.adminLpfrBlock(v.type, v.value, reason);
    },
    onSuccess: refresh,
  });
  const btn = (label: string, type: string, value: string | null, on: boolean) =>
    value ? (
      <Button size="sm" variant="secondary" onClick={() => toggle.mutate({ type, value, on: !on })}>
        {on ? `Odblokiraj ${label}` : `Blokiraj ${label}`}
      </Button>
    ) : null;
  const anyBlock = d.blocked.device || d.blocked.jid || d.blocked.tin || d.blocked.license;
  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-900">{d.companyName ?? "Nepoznata firma"}{d.companyTaxId ? ` · PIB ${d.companyTaxId}` : ""}</p>
          <p className="text-xs text-slate-500">{d.hostname ?? d.deviceModel ?? "uređaj"} · {d.platform ?? "?"} · v{d.version ?? "?"}</p>
        </div>
        <div className="flex gap-1">
          {d.noLicense && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">bez licence</span>}
          {anyBlock && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">blokirano</span>}
        </div>
      </div>
      <div className="mt-2 grid gap-1 text-sm text-slate-700 md:grid-cols-3">
        <p><span className="text-slate-500">JID: </span>{d.jid ?? "—"}</p>
        <p><span className="text-slate-500">IP: </span>{d.ip ?? "—"}</p>
        <p><span className="text-slate-500">Licenca: </span>{d.keyPrefix ? `${d.keyPrefix}…` : "—"}</p>
        <p><span className="text-slate-500">Prvo javljanje: </span>{fmt(d.firstSeenAt)}</p>
        <p><span className="text-slate-500">Poslednje: </span>{fmt(d.lastSeenAt)} ({d.calls}×)</p>
        <p><span className="text-slate-500">Ishod: </span>{OUTCOME[d.lastOutcome ?? ""] ?? d.lastOutcome ?? "—"}</p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {btn("uređaj", "DEVICE", d.machineId, d.blocked.device)}
        {btn("karticu (JID)", "JID", d.jid, d.blocked.jid)}
        {btn("firmu (PIB)", "TIN", d.tin ?? d.companyTaxId, d.blocked.tin)}
        {btn("licencu", "LICENSE", d.licenseKeyId, d.blocked.license)}
        <button className="text-xs text-slate-500 hover:underline" onClick={() => setHist(!hist)}>{hist ? "Sakrij istoriju" : "Istorija javljanja"}</button>
      </div>
      {hist && <History id={d.id} />}
    </div>
  );
}

export function LpfrDevicesTab({ companyId }: { companyId?: string } = {}) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const q = useQuery({ queryKey: ["lpfr-devices"], queryFn: () => api.adminLpfrDevices(), refetchInterval: 60000 });
  if (q.isLoading) return <Skeleton className="h-40 w-full" />;
  if (q.isError || !q.data) return <EmptyState icon={Cpu} title="Greška" description="Spisak uređaja nije dostupan." />;
  const refresh = () => qc.invalidateQueries({ queryKey: ["lpfr-devices"] });
  const f = filter.trim().toLowerCase();
  const list = q.data.devices.filter((d) => (!companyId || d.companyId === companyId) && (!f || [d.companyName, d.companyTaxId, d.jid, d.ip, d.hostname, d.deviceModel, d.keyPrefix].some((x) => x?.toLowerCase().includes(f))));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Pretraga: firma, PIB, JID, IP, računar" className="h-9 w-72 rounded-lg border border-border px-2 text-sm" />
        <span className="text-sm text-slate-500">
          Uređaja: {list.length} · bez licence: {list.filter((d) => d.noLicense).length} · aktivnih blokada: {q.data.blocks.length}
        </span>
      </div>
      {list.length === 0 ? <EmptyState icon={Cpu} title="Nema uređaja" description="Uređaji se pojavljuju kad se L-PFR prvi put prijavi serveru." /> : list.map((d) => <Card key={d.id} d={d} refresh={refresh} />)}
    </div>
  );
}
